package com.cs.ejobin.vm

import com.cs.ejobin.app.AppSession
import com.cs.ejobin.data.supabase.SupabaseExtrasRepository
import com.cs.ejobin.model.Extra
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

data class HomeUiState(
    val items: List<Extra> = emptyList(),
    val search: String = "",
    val selectedSkills: Set<String> = emptySet(),

    // ✅ toujours true (paiement désactivé)
    val hasDirectoryAccess: Boolean = true,

    val isRefreshing: Boolean = false,
    val isLoadingMore: Boolean = false,
    val endReached: Boolean = false,
    val error: String? = null
)

class HomeViewModel(
    private val extrasRepo: SupabaseExtrasRepository,
    private val session: AppSession // on le garde pour ne pas casser tes constructeurs, mais plus utilisé ici
) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Main.immediate)

    private val _state = MutableStateFlow(HomeUiState(isRefreshing = true))
    val state: StateFlow<HomeUiState> = _state.asStateFlow()

    private var pageSize = 20
    private var offset = 0
    private var searchJob: Job? = null
    private var refreshJob: Job? = null

    init {
        // ✅ plus de "session.state.collectLatest" / gating
        refresh()
    }

    fun onSearchChanged(value: String) {
        _state.value = _state.value.copy(search = value, error = null)
        searchJob?.cancel()
        searchJob = scope.launch {
            delay(350)
            refresh()
        }
    }

    fun toggleSkill(skill: String) {
        val cur = _state.value.selectedSkills
        val next = if (cur.contains(skill)) cur - skill else cur + skill
        _state.value = _state.value.copy(selectedSkills = next, error = null)
        refresh()
    }

    fun clearFilters() {
        _state.value = _state.value.copy(selectedSkills = emptySet(), error = null)
        refresh()
    }

    fun refresh() {
        refreshJob?.cancel()
        refreshJob = scope.launch {
            offset = 0
            _state.value = _state.value.copy(
                hasDirectoryAccess = true,
                isRefreshing = true,
                isLoadingMore = false,
                endReached = false,
                error = null
            )

            val result = withContext(Dispatchers.Default) {
                runCatching {
                    extrasRepo.listExtrasPaged(
                        query = _state.value.search,
                        skillFilters = _state.value.selectedSkills,
                        limit = pageSize,
                        offset = 0
                    )
                }
            }

            result.onSuccess { list ->
                offset = list.size
                _state.value = _state.value.copy(
                    items = list,
                    isRefreshing = false,
                    endReached = list.size < pageSize,
                    error = null
                )
            }.onFailure { e ->
                _state.value = _state.value.copy(
                    isRefreshing = false,
                    error = e.message ?: "Erreur de chargement"
                )
            }
        }
    }

    fun loadMoreIfNeeded(visibleIndex: Int) {
        val s = _state.value
        // ✅ plus de blocage sur hasDirectoryAccess
        if (s.isRefreshing || s.isLoadingMore || s.endReached) return
        if (visibleIndex < s.items.size - 6) return

        _state.value = s.copy(isLoadingMore = true, error = null)

        scope.launch {
            val result = withContext(Dispatchers.Default) {
                runCatching {
                    extrasRepo.listExtrasPaged(
                        query = s.search,
                        skillFilters = s.selectedSkills,
                        limit = pageSize,
                        offset = offset
                    )
                }
            }

            result.onSuccess { more ->
                offset += more.size
                _state.value = _state.value.copy(
                    items = _state.value.items + more,
                    isLoadingMore = false,
                    endReached = more.size < pageSize
                )
            }.onFailure { e ->
                _state.value = _state.value.copy(
                    isLoadingMore = false,
                    error = e.message ?: "Erreur pagination"
                )
            }
        }
    }

    fun dispose() {
        scope.cancel()
    }
}